export const PRODUCT_ITEM_ROLES = ['product', 'equipment', 'service', 'extra'] as const

export type ProductItemRole = (typeof PRODUCT_ITEM_ROLES)[number]

export const PRODUCT_ITEM_ROLE_LABELS: Record<ProductItemRole, string> = {
  product: 'Produs',
  equipment: 'Echipament',
  service: 'Serviciu',
  extra: 'Extra / accesoriu',
}

export const PRODUCT_ITEM_ROLE_HINTS: Record<ProductItemRole, string> = {
  product: 'Articol vândut / standard',
  equipment: 'Ex. închiriere, inventar returnabil',
  service: 'Serviciu, manoperă, abonament',
  extra: 'Accesoriu, consumabil, opțiune',
}

export const PRODUCT_ITEM_ROLE_OPTIONS = PRODUCT_ITEM_ROLES.map((value) => ({
  value,
  label: PRODUCT_ITEM_ROLE_LABELS[value],
  hint: PRODUCT_ITEM_ROLE_HINTS[value],
}))

/** Extra catalog prices per role (main `price` stays the selling / product price). */
export const PRODUCT_ROLE_PRICE_KEYS = ['equipment', 'service', 'extra'] as const

export type ProductRolePriceKey = (typeof PRODUCT_ROLE_PRICE_KEYS)[number]

/** Equipment is replacement value for contracts — never shown on the storefront. */
export const STOREFRONT_ROLE_PRICE_KEYS = ['service', 'extra'] as const satisfies readonly ProductRolePriceKey[]

export type ProductRolePrices = Partial<Record<ProductRolePriceKey, number | null>>

function parseRolePriceAmount(raw: unknown): number | null {
  if (raw === '' || raw == null) return null
  const amount = Number(raw)
  if (!Number.isFinite(amount) || amount < 0) return null
  return Math.round(amount * 100) / 100
}

export function parseRolePrices(raw: unknown): ProductRolePrices {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw)
    ? raw as Record<string, unknown>
    : {}
  const parsed: ProductRolePrices = {}
  for (const key of PRODUCT_ROLE_PRICE_KEYS) {
    const amount = parseRolePriceAmount(source[key])
    if (amount != null) parsed[key] = amount
  }
  return parsed
}

export function emptyRolePrices(): ProductRolePrices {
  return { equipment: undefined, service: undefined, extra: undefined }
}

export function listedRolePrices(
  raw: unknown,
  roles: readonly ProductRolePriceKey[] = PRODUCT_ROLE_PRICE_KEYS,
): Array<{
  role: ProductRolePriceKey
  label: string
  price: number
}> {
  const prices = parseRolePrices(raw)
  return roles
    .filter((role) => prices[role] != null)
    .map((role) => ({
      role,
      label: PRODUCT_ITEM_ROLE_LABELS[role],
      price: prices[role] as number,
    }))
}

export function rolePriceAmount(raw: unknown, role: ProductRolePriceKey): number | null {
  const amount = parseRolePrices(raw)[role]
  return amount != null && Number.isFinite(amount) ? amount : null
}

/** USelectMenu may bind the whole `{ value, label }` item instead of the string. */
export function unwrapSelectValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(unwrapSelectValue)
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>
    if ('value' in record) return unwrapSelectValue(record.value)
    if ('id' in record) return unwrapSelectValue(record.id)
  }
  return value
}

export function normalizeItemRole(value: unknown): ProductItemRole {
  const slug = String(unwrapSelectValue(value) || '').trim().toLowerCase()
  if (slug === 'equipment' || slug === 'echipament') return 'equipment'
  if (slug === 'service' || slug === 'serviciu') return 'service'
  if (slug === 'extra' || slug === 'accesoriu' || slug === 'accessory') return 'extra'
  if (slug === 'product' || slug === 'produs') return 'product'
  return 'product'
}

export function effectiveItemRole(value: unknown, enabled: boolean): ProductItemRole {
  return enabled ? normalizeItemRole(value) : 'product'
}
